import { Plate } from './Card';
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';

const fmt = v => new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 0,
}).format(v || 0);
const fmtCash = v => {
  if (v === null || v === undefined) return '—';
  const abs = fmt(Math.abs(v || 0));
  if ((v || 0) > 0) return `+${abs}`;
  if ((v || 0) < 0) return `−${abs}`;
  return abs;
};
const cashColor = v => (v || 0) > 0 ? '#0e9f6e' : (v || 0) < 0 ? '#e04848' : 'var(--text2)';

const getCurrentPeriod = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const buildPeriods = () => {
  const now = new Date();
  return Array.from({ length: 48 }, (_, idx) => {
    const d = new Date(now.getFullYear(), now.getMonth() - idx, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
};

function getProjection(revenue, period) {
  if (!period || !revenue) return null;
  const [year, month] = period.split('-').map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const now = new Date();
  if (year !== now.getFullYear() || month !== (now.getMonth() + 1)) return null;
  const daysPassed = now.getDate();
  const dailyAvg = revenue / daysPassed;
  const projection = Math.round(dailyAvg * daysInMonth);
  return { projection, dailyAvg, daysPassed, daysInMonth, growth: projection - revenue };
}

const getPrevPeriod = (periods, period) => {
  const idx = periods.indexOf(period);
  return idx >= 0 && idx < periods.length - 1 ? periods[idx + 1] : null;
};

const MonthArrows = ({ period, periods, onChange }) => {
  const idx = periods.indexOf(period);
  const canOlder = idx >= 0 && idx < periods.length - 1;
  const canNewer = idx > 0;
  const buttonStyle = enabled => ({
    width: 22, height: 22, borderRadius: 7, border: '1px solid var(--border)',
    background: enabled ? 'rgba(0,167,220,0.15)' : 'transparent',
    color: enabled ? '#00a7dc' : 'var(--text2)',
    cursor: enabled ? 'pointer' : 'default',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
    fontSize: 11, lineHeight: 1,
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
      <button type="button" onClick={() => canOlder && onChange(periods[idx + 1])} disabled={!canOlder} style={buttonStyle(canOlder)}>◀</button>
      <span style={{ fontSize: 11, color: 'var(--text2)', whiteSpace: 'nowrap' }}>Пред. месяц</span>
      <button type="button" onClick={() => canNewer && onChange(periods[idx - 1])} disabled={!canNewer} style={buttonStyle(canNewer)}>▶</button>
    </div>
  );
};

const MetricCard = ({ icon, label, value, sub, color }) => (
  <div style={{
    background: 'var(--bg3)',
    borderRadius: 14,
    padding: '16px 18px 18px',
    flex: 1,
    minWidth: 170,
  }}>
    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 7 }}>{icon} {label}</div>
    <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>{sub}</div>}
  </div>
);

const mapFranchiseItems = ({ stores, prevStores, live, title }) => {
  const prevMap = Object.fromEntries((prevStores || []).map(item => [item.store_external_id, item]));
  const liveMap = Object.fromEntries((live?.items || []).map(item => [item.store_external_id, item]));
  const baseStores = live?.items?.length ? live.items : (stores || []);
  return baseStores.map(store => {
    const externalId = store.store_external_id;
    const prev = prevMap[externalId];
    const liveItem = liveMap[externalId] || store || {};
    const dbStore = (stores || []).find(s => s.store_external_id === externalId) || {};
    const revenue = liveItem.revenue ?? liveItem.turnover ?? dbStore.revenue ?? store.revenue ?? 0;
    const receiptsCount = liveItem.receipts_count ?? dbStore.receipts_count ?? store.receipts_count ?? 0;
    const returnsAmount = liveItem.returns_amount ?? dbStore.returns_amount ?? store.returns_amount ?? 0;
    const royalty = Math.round((revenue || 0) * 0.05);
    return {
      ...store,
      id: externalId,
      name: store.name || store.store_name,
      store_name: store.store_name || store.name,
      partner_name: store.partner_name || '',
      company: title,
      token_status: store.token_status || 'active',
      token_label: store.token_label || 'МойСклад',
      has_moysklad_token: true,
      revenue,
      turnover: revenue,
      receipts_count: receiptsCount,
      returns_amount: returnsAmount,
      avg_check: receiptsCount ? Math.round(revenue / receiptsCount) : 0,
      today_revenue: liveItem.today_revenue || 0,
      today_cash: liveItem.today_cash || 0,
      today_noncash: liveItem.today_noncash || 0,
      today_count: liveItem.today_count || 0,
      cash_balance: liveItem.cash_balance ?? dbStore.cash_balance ?? store.cash_balance ?? null,
      daily: liveItem.daily || [],
      prev_month_revenue: liveItem.prev_month_revenue ?? prev?.revenue ?? 0,
      royalty_expected: royalty,
      total_expected: royalty,
      total_paid: 0,
      debt: royalty,
    };
  });
};

const EmptyPartnerCard = ({ color, networkSize }) => (
  <div style={{
    background: 'var(--bg2)',
    borderRadius: 14,
    padding: '18px 20px',
    border: '1px dashed var(--border)',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text1)' }}>Точки сети</div>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
          После подключения токена МойСклад здесь появятся карточки партнёров, как в ФДМ.
        </div>
      </div>
      <span style={{
        background: `${color}18`,
        color,
        border: `1px solid ${color}35`,
        borderRadius: 999,
        padding: '4px 10px',
        height: 24,
        fontSize: 12,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}>
        {networkSize}
      </span>
    </div>

    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: 10,
      marginTop: 18,
    }}>
      {[
        ['Сегодня', fmt(0)],
        ['Месяц', fmt(0)],
        ['Пред. месяц', fmt(0)],
      ].map(([label, value]) => (
        <div key={label} style={{ background: 'var(--bg3)', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 11, color: 'var(--text2)' }}>{label}</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text1)', marginTop: 3 }}>{value}</div>
        </div>
      ))}
    </div>
  </div>
);

const PartnerCard = ({ item, color, period, periods, totalRevenue, onPeriodChange }) => {
  const [dayIdx, setDayIdx] = useState(null);
  const active = item.token_status === 'active';
  const badgeColor = active ? '#0e9f6e' : item.token_status === 'error' ? '#e04848' : '#e08a00';
  const avgCheck = item.receipts_count ? Math.round((item.revenue || 0) / item.receipts_count) : 0;
  const prevRevenue = item.prev_month_revenue || 0;
  const shareOfTotal = totalRevenue ? ((item.revenue || 0) / totalRevenue * 100) : 0;
  const proj = getProjection(item.revenue || 0, period);
  const trendValue = proj ? proj.projection : (item.revenue || 0);
  const trendDelta = prevRevenue > 0 ? ((trendValue - prevRevenue) / prevRevenue * 100) : null;
  const days = item.daily || [];
  const today = new Date().toISOString().slice(0, 10);
  const fallbackIdx = days.findIndex(d => d.date === today);
  const currentDayIdx = dayIdx ?? (fallbackIdx >= 0 ? fallbackIdx : days.length - 1);
  const cashBalance = item.cash_balance ?? null;

  return (
    <div style={{
      background: 'var(--bg2)', borderRadius: 12, padding: '16px 20px',
      border: '1px solid var(--border)',
      borderLeft: `4px solid ${active ? color : badgeColor}`,
      transition: 'transform 0.1s', cursor: 'default',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color, marginBottom: 10, lineHeight: 1.3 }}>
            {item.store_name || item.name}
          </div>
          <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: -6, marginBottom: 10 }}>{item.company}</div>
        </div>
        <span style={{ background: `${badgeColor}18`, color: badgeColor, border: `1px solid ${badgeColor}35`, borderRadius: 999, padding: '4px 9px', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' }}>
          {item.token_label}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text2)' }}>Выручка</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0e9f6e' }}>{fmt(item.revenue)}</div>
        </div>
        <div>
          <MonthArrows period={period} periods={periods} onChange={onPeriodChange} />
          <div style={{ fontSize: 18, fontWeight: 700, color: prevRevenue > 0 ? 'var(--text1)' : 'var(--text2)' }}>
            {prevRevenue > 0 ? fmt(prevRevenue) : '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text2)' }}>Средний чек</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#00a7dc' }}>{fmt(avgCheck)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text2)' }}>Возвраты</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: (item.returns_amount || 0) > 0 ? '#e04848' : 'var(--text2)' }}>{fmt(item.returns_amount || 0)}</div>
        </div>
      </div>

      {active && (days.length > 0 || cashBalance !== null) && (() => {
        const idx = Math.max(0, Math.min(currentDayIdx, Math.max(0, days.length - 1)));
        const day = days[idx] || { date: today, revenue: 0, cash: 0, noncash: 0, count: 0 };
        const isToday = day.date === today;
        const dayLabel = day.date ? new Date(day.date + 'T12:00:00').toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
        const canPrev = idx > 0;
        const canNext = idx < days.length - 1;

        return (
        <div style={{
          marginTop: 10, marginBottom: 10, padding: '10px 12px', borderRadius: 8,
          background: 'rgba(0,167,220,0.06)', border: '1px solid rgba(0,167,220,0.2)',
        }}>
          {cashBalance !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 10, color: cashColor(cashBalance), textTransform: 'uppercase', fontWeight: 600 }}>💰 В кассе</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: cashColor(cashBalance) }}>{fmtCash(cashBalance)}</span>
            </div>
          )}
          {days.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                <button onClick={() => canPrev && setDayIdx(idx - 1)} disabled={!canPrev} style={{
                  background: canPrev ? 'rgba(0,167,220,0.15)' : 'transparent',
                  color: canPrev ? '#00a7dc' : 'var(--text2)',
                  border: '1px solid var(--border)', borderRadius: 8,
                  width: 28, height: 28, fontSize: 14, cursor: canPrev ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                }}>◀</button>
                <div style={{ textAlign: 'center', minWidth: 100 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)' }}>{dayLabel}</div>
                  <div style={{ fontSize: 10, color: isToday ? '#0e9f6e' : 'var(--text2)', fontWeight: 600 }}>
                    {isToday ? '● сегодня' : day.date}
                  </div>
                </div>
                <button onClick={() => canNext && setDayIdx(idx + 1)} disabled={!canNext} style={{
                  background: canNext ? 'rgba(0,167,220,0.15)' : 'transparent',
                  color: canNext ? '#00a7dc' : 'var(--text2)',
                  border: '1px solid var(--border)', borderRadius: 8,
                  width: 28, height: 28, fontSize: 14, cursor: canNext ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                }}>▶</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                <div style={{ background: 'rgba(14,159,110,0.1)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: 'var(--text2)', textTransform: 'uppercase' }}>Выручка</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#0e9f6e' }}>{fmt(day.revenue)}</div>
                </div>
                <div style={{ background: 'rgba(14,159,110,0.08)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: 'var(--text2)', textTransform: 'uppercase' }}>💵 Нал</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0e9f6e' }}>{fmt(day.cash)}</div>
                </div>
                <div style={{ background: 'rgba(0,167,220,0.08)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: 'var(--text2)', textTransform: 'uppercase' }}>💳 Безнал</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#00a7dc' }}>{fmt(day.noncash)}</div>
                </div>
              </div>

              <div style={{ textAlign: 'center', marginTop: 6, fontSize: 11, color: 'var(--text2)' }}>
                🧾 {day.count} {day.count === 1 ? 'чек' : day.count < 5 ? 'чека' : 'чеков'}
                {day.count > 0 && <span style={{ marginLeft: 8 }}>· ср. чек {fmt(Math.round(day.revenue / day.count))}</span>}
              </div>
            </>
          )}
        </div>
        );
      })()}

      {active && prevRevenue > 0 && trendDelta !== null && (
        <div style={{
          marginTop: 10, padding: '8px 12px', borderRadius: 8,
          background: trendDelta >= 0 ? 'rgba(14,159,110,0.08)' : 'rgba(224,72,72,0.08)',
          border: `1px solid ${trendDelta >= 0 ? 'rgba(14,159,110,0.25)' : 'rgba(224,72,72,0.25)'}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--text2)' }}>{proj ? '📈 Тренд vs пред. месяц' : 'vs пред. месяц'}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: trendDelta >= 0 ? '#0e9f6e' : '#e04848' }}>
              {trendDelta >= 0 ? '↑' : '↓'} {Math.abs(trendDelta).toFixed(1)}%
            </span>
          </div>
          {proj && <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 3 }}>Прогноз {fmt(proj.projection)} vs {fmt(prevRevenue)}</div>}
        </div>
      )}

      {active && proj && (
        <div style={{
          marginTop: 10, padding: '8px 10px', borderRadius: 8,
          background: 'rgba(0,167,220,0.08)', border: '1px solid rgba(0,167,220,0.2)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text2)' }}>📈 Прогноз на месяц</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#00a7dc' }}>{fmt(proj.projection)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text2)', marginBottom: 5 }}>
            <span>+{fmt(proj.growth)} за {proj.daysInMonth - proj.daysPassed} дн.</span>
            <span>{fmt(Math.round(proj.dailyAvg))}/день · {proj.daysPassed}/{proj.daysInMonth} дн.</span>
          </div>
          <div style={{ height: 3, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(proj.daysPassed / proj.daysInMonth) * 100}%`, background: '#00a7dc', borderRadius: 2 }} />
          </div>
        </div>
      )}

      {active && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>
            <span>Доля в общей выручке</span>
            <span>{shareOfTotal.toFixed(1)}%</span>
          </div>
          <div style={{ height: 4, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(shareOfTotal, 100)}%`, background: color, borderRadius: 2 }} />
          </div>
        </div>
      )}

      {active && (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '4px 0', color: 'var(--text2)' }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>📤 Расходы (выдачи из кассы)</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--text2)' }}>нет данных</span>
              <span style={{ display: 'inline-block', fontSize: 14 }}>▾</span>
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginTop: 12 }}>
            <div style={{ background: 'rgba(0,167,220,0.08)', border: '1px solid rgba(0,167,220,0.2)', borderRadius: 9, padding: '8px 10px' }}>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>Роялти к оплате</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#00a7dc' }}>{fmt(item.royalty_expected)}</div>
            </div>
            <div style={{ background: (item.debt || 0) > 0 ? 'rgba(224,72,72,0.08)' : 'rgba(14,159,110,0.08)', border: '1px solid var(--border)', borderRadius: 9, padding: '8px 10px' }}>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>Долг</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: (item.debt || 0) > 0 ? '#e04848' : '#0e9f6e' }}>{fmt(item.debt)}</div>
            </div>
          </div>
        </div>
      )}

      {!active && (
        <div style={{ color: '#e08a00', fontSize: 12, marginTop: 12 }}>
          {item.token_status === 'error' ? 'МойСклад не отдал данные по этому токену.' : 'Токен в таблице помечен как нерабочий, синхронизация не запущена.'}
        </div>
      )}
    </div>
  );
};

export default function FranchiseCompanyDashboard({
  icon,
  title,
  subtitle,
  color,
  networkSize,
  royaltyLabel = '5%',
  moneyTarget,
  tokenEnvName,
  sourceName = 'МойСклад',
  network,
}) {
  const periods = useMemo(buildPeriods, []);
  const [period, setPeriod] = useState(getCurrentPeriod());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(network));

  useEffect(() => {
    if (!network) return;
    let cancelled = false;
    setLoading(true);
    const [y, m] = period.split('-').map(Number);
    const prevPeriod = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
    Promise.all([
      api.get('/retail/stores/summary', { params: { period, store_type: network } }).then(r => r.data).catch(() => null),
      api.get('/retail/stores', { params: { period, store_type: network } }).then(r => r.data).catch(() => []),
      api.get('/retail/stores', { params: { period: prevPeriod, store_type: network } }).then(r => r.data).catch(() => []),
    ])
      .then(([summary, stores, prevStores]) => {
        if (cancelled) return;
        const items = mapFranchiseItems({ stores, prevStores, live: null, title });
        setData({
          period,
          count: items.length,
          active_tokens: items.length,
          not_working_tokens: 0,
          revenue: summary?.total_revenue || items.reduce((sum, item) => sum + (item.revenue || 0), 0),
          today_revenue: 0,
          cash_total: null,
          royalty_expected: Math.round((summary?.total_revenue || items.reduce((sum, item) => sum + (item.revenue || 0), 0)) * 0.05),
          items,
        });
        setLoading(false);

        api.get(`/franchise/${network}/partners`, { params: { period }, timeout: 300000 })
          .then(r => r.data)
          .then(live => {
            if (cancelled) return;
            const liveItems = mapFranchiseItems({ stores, prevStores, live, title });
            const liveRevenue = live?.revenue ?? liveItems.reduce((sum, item) => sum + (item.revenue || 0), 0);
            setData({
              period,
              count: liveItems.length,
              active_tokens: live?.active_tokens ?? liveItems.length,
              not_working_tokens: live?.not_working_tokens ?? 0,
              revenue: liveRevenue,
              today_revenue: live?.today_revenue || 0,
              cash_total: live?.cash_total ?? null,
              royalty_expected: Math.round((liveRevenue || 0) * 0.05),
              items: liveItems,
            });
          })
          .catch(() => {});
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [network, period, title]);

  const partners = data?.items || [];
  const totalCount = data?.count ?? networkSize;
  const activeTokens = data?.active_tokens ?? 0;
  const notWorkingTokens = data?.not_working_tokens ?? 0;

  return (
    <div>
      <div className="kp-hero" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
        <div>
          <Plate>Франшиза · партнёры</Plate>
          <h1 style={{ margin: 0 }}>{title}</h1>
          <div style={{ color: 'var(--text2)', marginTop: 8, fontSize: 13 }}>{subtitle}</div>
        </div>

        <select
          value={period}
          onChange={e => setPeriod(e.target.value)}
          style={{
            background: 'var(--bg2)',
            color: 'var(--text1)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '9px 12px',
            fontSize: 13,
          }}
        >
          {periods.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 18 }}>
        <MetricCard icon="🏪" label="Точек в сети" value={loading ? '...' : totalCount} sub={partners.length ? `загружено: ${partners.length}` : 'будут загружены из МойСклад'} color={color} />
        <MetricCard icon="💰" label="Оборот за период" value={fmt(data?.revenue || 0)} sub={`период ${period}`} color="#0e9f6e" />
        <MetricCard icon="☀️" label="Выручка сегодня" value={fmt(data?.today_revenue || 0)} sub="по кассам МойСклад" color="#0e9f6e" />
        <MetricCard icon="💵" label="Денег в кассах" value={fmtCash(data?.cash_total)} sub="наличные по сменам" color="#e08a00" />
        <MetricCard icon="🤝" label="Роялти к оплате" value={fmt(data?.royalty_expected || 0)} sub={`${royaltyLabel} · ${moneyTarget}`} color="#00a7dc" />
        <MetricCard
          icon="🔌"
          label="Источник данных"
          value={partners.length ? `${activeTokens} ток.` : 'Ожидает токен'}
          sub={notWorkingTokens ? `не работает: ${notWorkingTokens}` : tokenEnvName}
          color={notWorkingTokens ? '#e08a00' : color}
        />
      </div>

      <div style={{
        background: `${color}10`,
        border: `1px solid ${color}35`,
        borderRadius: 14,
        padding: '16px 18px',
        marginBottom: 18,
        display: 'flex',
        gap: 14,
      }}>
        <span style={{ fontSize: 26 }}>🔌</span>
        <div>
          <div style={{ color, fontWeight: 800, fontSize: 15 }}>
            {partners.length ? `Точки ${sourceName} загружены` : `Подключение к ${sourceName} подготовлено`}
          </div>
          <div style={{ color: 'var(--text1)', fontSize: 13, lineHeight: 1.55, marginTop: 4 }}>
            {partners.length
              ? `Данные берутся напрямую из МойСклад по каждому токену: кассы/филиалы, дневная выручка, нал/безнал, деньги в кассе, месяц, предыдущий месяц и роялти.`
              : <>Сейчас раздел показан в интерфейсе как ФДМ. Когда дадите токен, добавим его в переменную <b>{tokenEnvName}</b>,
                подтянем точки, оборот, ежедневную выручку, предыдущий месяц, роялти и долги партнёров.</>}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.35fr) minmax(300px, 0.65fr)', gap: 16 }}>
        <div>
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Партнёры / точки</h2>
            <span style={{ color: 'var(--text2)', fontSize: 13 }}>
              {loading ? 'загрузка...' : partners.length ? `${partners.length} загружено` : 'ожидают синхронизацию'}
            </span>
          </div>
          {partners.length ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
              {partners.map(item => (
                <PartnerCard
                  key={item.id}
                  item={item}
                  color={color}
                  period={period}
                  periods={periods}
                  totalRevenue={data?.revenue || 0}
                  onPeriodChange={setPeriod}
                />
              ))}
            </div>
          ) : (
            <EmptyPartnerCard color={color} networkSize={networkSize} />
          )}
        </div>

        <div style={{
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          padding: '18px 20px',
          alignSelf: 'start',
        }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 12 }}>{partners.length ? 'Что уже отображается' : 'Что подключим после токена'}</div>
          {[
            'Список точек и партнёров',
            'Оборот за выбранный месяц',
            'Ежедневную выручку в карточке',
            'Деньги в кассе, нал/безнал',
            'Сравнение с предыдущим месяцем',
            'Начисление роялти и задолженность',
          ].map((item, idx) => (
            <div key={item} style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              padding: '9px 0',
              borderTop: idx ? '1px solid var(--border)' : 0,
            }}>
              <span style={{ color, fontWeight: 900 }}>✓</span>
              <span style={{ color: 'var(--text1)', fontSize: 13 }}>{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
